import { cloneDeep, find, get, groupBy } from 'lodash'
import { handleActions } from 'redux-actions'
import { createSelector } from 'reselect'

import { catchError } from '@emmacare/common/api/httpClient'

import { setCompanyReferences } from 'src/store/modules/actionCreators'
import { companySelector } from 'src/store/modules/company/selectors'
import { isUrgentCareTriageNurseEnabled$ } from 'src/store/modules/user/selectors'

import {
  NO_GROUP_DROPDOWN_VALUE,
  NO_OUTCOME_VALUE,
  NO_PREVENTIVE_TYPE_VALUE,
  NO_REFERRAL_REASON,
  URGENT_CARE_TRIAGE_OUTCOME_SUBTYPES,
  refTypes
} from 'src/constants'
import { getCompanyReferences as getCompanyReferencesAPI } from 'src/scenes/Admin/ReferenceTable/api'
import { REFERRAL_OUTREACH_STATUSES_GROUPS } from 'src/scenes/Referral/constants'
import { addAllValue, convertDataFields } from 'src/utils/DataTrees'
import { mapListForDropdownV5 } from 'src/utils/v5/Array'
import {
  addAllValue as addAllValueV5,
  convertDataFields as convertDataFieldsV5
} from 'src/utils/v5/DataTrees'

const refsPattern = Array.from(refTypes).reduce(
  (acc = {}, [name]) => ({
    ...acc,
    [name]: []
  }),
  {}
)

/* --------- reducer --------- */
const initialState = { ...refsPattern }

export default handleActions(
  {
    [setCompanyReferences](state, { payload: references }) {
      return {
        ...state,
        ...references
      }
    }
  },
  initialState
)

/* --------- helpers --------- */
const extractReferences = (refs) => {
  const references = cloneDeep(initialState)

  if (!Array.isArray(refs) || refs.length === 0) {
    return references
  }

  Object.keys(initialState).forEach((refKey) => {
    references[refKey] = refs.filter((r) => r.type === refTypes.get(refKey))
  })

  return references
}

/* --------- thunks --------- */
export const getCompanyReferences =
  (query = {}) =>
  async (dispatch) => {
    const config = {
      query: {
        ...query,
        sort: 'refValue:asc'
      }
    }

    try {
      const { items } = await getCompanyReferencesAPI(config)

      dispatch(setCompanyReferences(extractReferences(items)))
    } catch (err) {
      catchError(err)
      Promise.reject(err)
    }
  }

/* --------- selectors --------- */
export const companyReferenceTableSelector = createSelector(
  companySelector,
  (company) => company.references
)

export const companyPhoneTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.phones
)

export const companyOnHoldReasonTreeSelector = createSelector(
  companyReferenceTableSelector,
  (refs) =>
    addAllValueV5({
      list: refs.patientHoldReason || []
    })
)

export const companyOnHoldReasonOptionsSelector = createSelector(
  companyReferenceTableSelector,
  (refs) =>
    refs.patientHoldReason?.map((reason) => ({
      value: reason.uuid,
      key: reason.uuid,
      title: reason.refValue,
      label: reason.refValue
    }))
)

export const companyOnHoldReasonSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.patientHoldReason
)

export const companyEmailTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.emails
)

export const companyMeasurementMethodsSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.measurementMethod
)

export const companyGenderTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) =>
    refs.genders.map((item) => ({
      ...item,
      key: item.uuid,
      value: item.uuid,
      title: item.refValue
    }))
)

export const companyGenderTypes$ = createSelector(
  companyReferenceTableSelector,
  (refs) =>
    refs.genders.map(({ refValue, uuid }) => ({
      key: uuid,
      label: refValue,
      value: uuid
    }))
)

export const companyAddressTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.addresses
)

export const companyContactTypes = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.relationship
)

export const companyMedicalSpecialtiesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.medicalSpecialties
)

export const companyMedicalSpecialtiesSelectorDropdown = createSelector(
  companyMedicalSpecialtiesSelector,
  (items = []) =>
    items.map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue,
      label: refValue
    }))
)

export const companyDischargeProgramReasonsSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.programDischargeReasons
)

export const externalReferralType$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.externalReferralType
)

export const externalReferralTypeDropdown = createSelector(
  companyReferenceTableSelector,
  (refs) =>
    refs.externalReferralType.map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue,
      label: refValue
    }))
)

export const companyDischargeProgramReasonsSelectorDropdown = createSelector(
  companyDischargeProgramReasonsSelector,
  (programDischargeReasons = []) =>
    programDischargeReasons.map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue
    }))
)

export const companyDischargeProgramReasonsDropdownWithAll$ = createSelector(
  companyDischargeProgramReasonsSelector,
  (programDischargeReasons = []) => addAllValue(programDischargeReasons, true)
)

export const companyPatientGroupsFilterSelector = createSelector(
  companyReferenceTableSelector,
  (refs) =>
    addAllValueV5({
      list: [
        ...(Array.isArray(refs.patientGroups) ? refs.patientGroups : []),
        NO_GROUP_DROPDOWN_VALUE
      ],
      convertList: true
    })
)

export const companyPatientGroupsSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.patientGroups
)

export const companyPatientGroupsFilterDropdown$ = createSelector(
  companyPatientGroupsSelector,
  (patientGroups = []) =>
    patientGroups.map((item) => ({
      key: item.uuid,
      value: item.uuid,
      title: item.refValue,
      label: item.refValue
    }))
)

export const companyLanguageTypesSelector = createSelector(
  companyReferenceTableSelector,
  ({ languages }) =>
    languages.map((item) => ({
      ...item,
      title: item.refValue,
      value: item.uuid
    }))
)

export const companyLanguagesDropdown$ = createSelector(
  companyReferenceTableSelector,
  ({ languages = [] }) => addAllValueV5({ list: languages })
)

export const companyEnglishUuid$ = createSelector(
  companyLanguageTypesSelector,
  (languages = []) => {
    const englishLanguage = find(languages, { title: 'English' })
    const englishUuid = get(englishLanguage, ['uuid'], null)

    return englishUuid
  }
)

export const companyActivityTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.activityTypes
)

export const companyAuditLogTimerSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.auditLogTimer
)

export const companyEducationRefSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.education
)

export const companyCheckInRefSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.checkIn
)

export const companyMeasurementRefSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.measurement
)

export const companyChatRefSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.chat
)

export const companyBroadcastCategoryList$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.broadcastCategoryList
)

export const eventTypes$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.eventTypes
)

export const enrollProgramType$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.enrollProgramType
)

export const enrollProgramSelect$ = createSelector(
  enrollProgramType$,
  (enrollProgramType = []) =>
    enrollProgramType.map((item) => ({
      key: item.uuid,
      value: item.uuid,
      title: item.refValue,
      label: item.refValue
    }))
)

export const enrollProgramTypeDropdown$ = createSelector(
  enrollProgramType$,
  (enrollProgramType = []) =>
    enrollProgramType.map((item) => ({
      ...item,
      key: item.uuid,
      value: item.uuid,
      title: item.refValue
    }))
)

export const enrollProgramTypeWithAll$ = createSelector(
  enrollProgramType$,
  (enrollProgramType = []) => addAllValue(enrollProgramType, true)
)

export const medicationSkipReason$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.medicationSkipReason
)

export const backfillMedication$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.backfillMedication
)

export const backfillCarePlan$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.backfillCarePlan
)

export const patientProviderTypes$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.patientProvider
)

export const patientInsuranceTypes$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.patientInsurance
)

export const patientHoldReason$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.patientHoldReason
)

export const patientHoldReasonDropdown$ = createSelector(
  patientHoldReason$,
  (patientHoldReason = []) =>
    patientHoldReason.map((item) => ({
      key: item.uuid,
      label: item.refValue,
      title: item.refValue,
      value: item.uuid
    }))
)

export const prognosis$ = createSelector(
  companyReferenceTableSelector,
  ({ prognosis }) =>
    prognosis.map((item) => ({
      ...item,
      value: item.uuid,
      title: item.refValue
    }))
)

export const companyTelemedicineRefs$ = createSelector(
  companyReferenceTableSelector,
  ({ telemedicine }) => telemedicine
)

export const companyRiskLevelRefs$ = createSelector(
  companyReferenceTableSelector,
  ({ riskLevel }) => riskLevel
)

export const companyEmergencyPreparednessRiskLevelRefs$ = createSelector(
  companyReferenceTableSelector,
  ({ emergencyPreparednessRiskLevel }) => emergencyPreparednessRiskLevel
)

export const companyModeOfTransportationRefs$ = createSelector(
  companyReferenceTableSelector,
  ({ modeOfTransportation }) => modeOfTransportation
)

export const companyFileTypeRefs$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.externalDocumentType
)

export const companyFileTypeRefsDropdown$ = createSelector(
  companyFileTypeRefs$,
  (externalDocumentType) => convertDataFields(externalDocumentType)
)

export const companyFileTypeRefsWithAll$ = createSelector(
  companyFileTypeRefs$,
  (externalDocumentType) => addAllValue(externalDocumentType, true)
)

export const companyAlertQuickCloseReasons$ = createSelector(
  companyReferenceTableSelector,
  ({ alertQuickCloseReasons }) => alertQuickCloseReasons
)

export const medicalEquipmentStatuses$ = createSelector(
  companyReferenceTableSelector,
  ({ medicalEquipmentStatuses = [] }) => medicalEquipmentStatuses
)

export const medicalEquipmentStatusesDropdown$ = createSelector(
  medicalEquipmentStatuses$,
  (medicalEquipmentStatuses = []) => convertDataFields(medicalEquipmentStatuses)
)

export const medicalEquipmentCategories$ = createSelector(
  companyReferenceTableSelector,
  ({ medicalEquipmentCategories = [] }) => medicalEquipmentCategories
)

export const medicalEquipmentCategoriesDropdown$ = createSelector(
  medicalEquipmentCategories$,
  (medicalEquipmentCategories = []) =>
    convertDataFields(medicalEquipmentCategories)
)

export const medicalDeviceTrainingStatus$ = createSelector(
  companyReferenceTableSelector,
  ({ medicalDeviceTrainingStatus = [] }) => medicalDeviceTrainingStatus
)

export const medicalDeviceTrainingStatusDropdown$ = createSelector(
  medicalDeviceTrainingStatus$,
  (medicalDeviceTrainingStatus = []) =>
    medicalDeviceTrainingStatus.map(({ refValue: title, uuid: value }) => ({
      title,
      value,
      key: value
    }))
)

export const medicalDeviceTrainingStatusDropdownV5$ = createSelector(
  medicalDeviceTrainingStatus$,
  (medicalDeviceTrainingStatus = []) =>
    medicalDeviceTrainingStatus.map(({ refValue: label, uuid: value }) => ({
      key: value,
      label,
      value
    }))
)

export const medicalDeviceTrainingStatusWithAll$ = createSelector(
  medicalDeviceTrainingStatus$,
  (medicalDeviceTrainingStatus = []) =>
    addAllValue(medicalDeviceTrainingStatus, true)
)

export const medicalDeviceShipmentStatus$ = createSelector(
  companyReferenceTableSelector,
  ({ medicalDeviceShipmentStatus = [] }) => medicalDeviceShipmentStatus
)

export const medicalDeviceShipmentStatusDropdown$ = createSelector(
  medicalDeviceShipmentStatus$,
  (medicalDeviceShipmentStatus = []) =>
    medicalDeviceShipmentStatus.map(({ refValue: title, uuid: value }) => ({
      title,
      value,
      key: value
    }))
)

export const medicalDeviceShipmentStatusWithAll$ = createSelector(
  medicalDeviceShipmentStatus$,
  (medicalDeviceShipmentStatus = []) =>
    addAllValue(medicalDeviceShipmentStatus, true)
)

export const telemedicineCallType$ = createSelector(
  companyReferenceTableSelector,
  ({ telemedicineCallType = [] }) => telemedicineCallType
)

export const telemedicineCallTypeDropdown$ = createSelector(
  telemedicineCallType$,
  (telemedicineCallType = []) =>
    telemedicineCallType
      .filter(({ enabled }) => enabled)
      .map(
        ({
          patientNotificationType,
          refValue,
          uuid,
          defaultCallDuration,
          sendConfirmation,
          subType
        }) => ({
          patientNotificationType,
          key: uuid,
          value: uuid,
          title: refValue,
          label: refValue,
          defaultCallDuration,
          sendConfirmation,
          subType
        })
      )
)

export const telemedicineCallTypeDropdownWithAll$ = createSelector(
  telemedicineCallTypeDropdown$,
  (telemedicineCallType = []) => addAllValue(telemedicineCallType)
)

export const telemedicineCallTypesDropdownWithAllV5$ = createSelector(
  telemedicineCallType$,
  (telemedicineCallType = []) =>
    addAllValueV5({
      list: telemedicineCallType.filter(({ enabled }) => enabled)
    })
)

export const taskStatus$ = createSelector(
  companyReferenceTableSelector,
  ({ taskStatus = [] }) => taskStatus
)

export const taskStatusDropdown$ = createSelector(
  taskStatus$,
  (taskStatus = []) =>
    taskStatus
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        title: refValue,
        label: refValue
      }))
)

export const taskStatusWithAll$ = createSelector(
  taskStatus$,
  (taskStatus = []) =>
    addAllValue(
      taskStatus.filter(({ enabled }) => enabled),
      true
    )
)

export const taskType$ = createSelector(
  companyReferenceTableSelector,
  ({ taskType = [] }) => taskType
)

export const taskTypeDropdown$ = createSelector(taskType$, (taskType = []) =>
  taskType
    .filter(({ enabled }) => enabled)
    .map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue
    }))
)

export const taskTypeDropdownV5$ = createSelector(taskType$, (taskType = []) =>
  taskType
    .filter(({ enabled }) => enabled)
    .map(({ refValue: label, uuid: value }) => ({
      key: value,
      label,
      value
    }))
)

export const taskTypeDropdownWithAll$ = createSelector(
  taskType$,
  (taskType = []) => addAllValue(taskType, true)
)

export const taskTypeDropdownWithAllV5$ = createSelector(
  taskType$,
  (taskType = []) => addAllValueV5({ list: taskType })
)

export const taskAction$ = createSelector(
  companyReferenceTableSelector,
  ({ taskAction = [] }) => taskAction
)

export const taskActionDropdown$ = createSelector(
  taskAction$,
  (taskAction = []) =>
    taskAction
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        title: refValue
      }))
)

export const urgentCareOutcome$ = createSelector(
  companyReferenceTableSelector,
  (company = {}) =>
    get(company, ['urgentCareOutcome'], []).map(
      ({ uuid, refValue, subType }) => ({
        key: uuid,
        value: uuid,
        title: refValue,
        label: refValue,
        subType
      })
    )
)

export const urgentCareOutcomeV5$ = createSelector(
  companyReferenceTableSelector,
  ({ urgentCareOutcome = [] }) =>
    urgentCareOutcome.map(({ uuid, refValue, subType }) => ({
      key: uuid,
      label: refValue,
      value: uuid,
      subType
    }))
)

// The triage outcome values ("Triage follow-up needed"/"completed", subTypes
// 1 & 2) are only selectable/visible when the company flag
// `isUrgentCareTriageNurse` is ON. Keep the ungated selectors above for any
// consumer that needs the full list, and expose gated variants below that every
// Outcome dropdown consumes.
const excludeTriageOutcomes = (options = [], isTriageNurse = false) =>
  isTriageNurse
    ? options
    : options.filter(
        ({ subType }) => !URGENT_CARE_TRIAGE_OUTCOME_SUBTYPES.includes(subType)
      )

export const urgentCareOutcomeGated$ = createSelector(
  urgentCareOutcome$,
  isUrgentCareTriageNurseEnabled$,
  excludeTriageOutcomes
)

export const urgentCareOutcomeV5Gated$ = createSelector(
  urgentCareOutcomeV5$,
  isUrgentCareTriageNurseEnabled$,
  excludeTriageOutcomes
)

// The scheduled Urgent Care Facility report always excludes calls with triage
// outcomes, so the report management outcome dropdown must never offer them —
// regardless of the "Triage Nurse" company flag.
export const urgentCareOutcomeV5WithoutTriage$ = createSelector(
  urgentCareOutcomeV5$,
  (options = []) => excludeTriageOutcomes(options, false)
)

export const telemedicineCallOutcomeDropdown$ = createSelector(
  companyReferenceTableSelector,
  (company = {}) =>
    get(company, ['telemedicineCallOutcome'], [])
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        title: refValue,
        label: refValue
      }))
)

export const companyReferralReasonTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.referralReasons
)

export const companyReferralReasonTypestWithAllAndNoOption$ = createSelector(
  companyReferralReasonTypesSelector,
  (referralReasons = []) =>
    addAllValueV5({ list: [...referralReasons, NO_REFERRAL_REASON] })
)

export const companyReferralOutreachStatusesTypesSelector = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.referralOutreachStatuses
)

export const companyReferralOutreachStatusesDropdown$ = createSelector(
  companyReferralOutreachStatusesTypesSelector,
  (referralOutreachStatuses = []) =>
    referralOutreachStatuses.map(mapListForDropdownV5)
)

export const companyReferralOutreachStatusesWithAll$ = createSelector(
  companyReferralOutreachStatusesTypesSelector,
  (referralOutreachStatuses = []) =>
    addAllValueV5({
      list: Object.entries(groupBy(referralOutreachStatuses, 'statusType')).map(
        ([key, value]) =>
          value.length > 1
            ? {
                key,
                title: REFERRAL_OUTREACH_STATUSES_GROUPS[key],
                value: value.map(({ uuid = '' }) => uuid).join(','),
                statusType: value[0].statusType,
                children: value
              }
            : value[0]
      ),
      fields: ['statusType']
    })
)

export const companyReferralOutreachStatuses$ = createSelector(
  companyReferralOutreachStatusesTypesSelector,
  (referralOutreachStatuses = []) =>
    convertDataFieldsV5(
      Object.entries(groupBy(referralOutreachStatuses, 'statusType')).map(
        ([key, value]) =>
          value.length > 1
            ? {
                key,
                title: REFERRAL_OUTREACH_STATUSES_GROUPS[key],
                value: value.map(({ uuid = '' }) => uuid).join(','),
                statusType: value[0].statusType,
                children: value
              }
            : value[0]
      ),
      'children',
      ['statusType']
    )
)

export const actionNotification$ = createSelector(
  companyReferenceTableSelector,
  ({ actionNotification = [] }) => actionNotification
)

export const actionNotificationDropdown$ = createSelector(
  companyReferenceTableSelector,
  ({ actionNotification = [] }) =>
    actionNotification
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        label: refValue,
        title: refValue
      }))
)

export const actionNotificationWithAll$ = createSelector(
  actionNotification$,
  (actionNotification = []) => addAllValue(actionNotification, true)
)

export const companyPreventiveTypes$ = createSelector(
  companyReferenceTableSelector,
  (refs = {}) => refs?.preventiveTypes || []
)

export const companyPreventiveTypesWithAll$ = createSelector(
  companyPreventiveTypes$,
  (preventiveTypes = []) =>
    addAllValueV5({
      list: [...preventiveTypes, NO_PREVENTIVE_TYPE_VALUE]
    })
)

export const insuranceProgramDropdown$ = createSelector(
  companyReferenceTableSelector,
  (references = []) =>
    addAllValueV5({
      list: references?.insuranceProgram || []
    })
)

export const companyUrgentCareOutcome$ = createSelector(
  companyReferenceTableSelector,
  (references = {}) =>
    addAllValueV5({
      list: [...(references?.urgentCareOutcome || []), NO_OUTCOME_VALUE]
    })
)

export const companyUrgentCareOutcomeGated$ = createSelector(
  companyReferenceTableSelector,
  isUrgentCareTriageNurseEnabled$,
  (references = {}, isTriageNurse = false) =>
    addAllValueV5({
      list: [
        ...excludeTriageOutcomes(
          references?.urgentCareOutcome || [],
          isTriageNurse
        ),
        NO_OUTCOME_VALUE
      ]
    })
)

export const insuranceProgramsWithAll$ = createSelector(
  companyReferenceTableSelector,
  (references = {}) =>
    addAllValueV5({
      list: references?.insuranceProgram || []
    })
)

export const patientProgressNoteTypes$ = createSelector(
  companyReferenceTableSelector,
  (references = {}) => references?.patientProgressNoteType || []
)

export const patientProgressNoteTypesDropdown$ = createSelector(
  patientProgressNoteTypes$,
  (patientProgressNoteType = []) =>
    patientProgressNoteType
      ?.filter(({ enabled = false }) => enabled)
      ?.map(mapListForDropdownV5) || []
)

export const appointmentStatuses$ = createSelector(
  companyReferenceTableSelector,
  (references = {}) => references?.appointmentStatuses || []
)

export const appointmentStatusesDropdown$ = createSelector(
  appointmentStatuses$,
  (appointmentStatuses = []) => appointmentStatuses.map(mapListForDropdownV5)
)

export const appointmentStatusesFilteredDropdown$ = createSelector(
  appointmentStatusesDropdown$,
  (appointmentStatuses = []) =>
    appointmentStatuses.filter(({ label = '' }) => !['Deleted'].includes(label))
)

export const appointmentStatusesDropdownWithAll$ = createSelector(
  appointmentStatusesDropdown$,
  (appointmentStatuses = []) =>
    addAllValueV5({
      convertList: false,
      list: appointmentStatuses
    })
)

export const appointmentStatusesPatientDropdownWithAll$ = createSelector(
  appointmentStatusesFilteredDropdown$,
  (appointmentStatuses = []) =>
    addAllValueV5({
      convertList: false,
      list: appointmentStatuses
    })
)

export const bhCareManager$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.bhCareManager)
)

export const bhCareManagerWithNo$ = createSelector(
  bhCareManager$,
  (bhCareManager) => [
    ...bhCareManager,
    {
      label: 'No BH Care Manager',
      value: 'no-bh-care-manager'
    }
  ]
)
export const psychConsultant$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.psychConsultant)
)

export const psychConsultantWithNo$ = createSelector(
  psychConsultant$,
  (psychConsultant) => [
    ...psychConsultant,
    {
      label: 'No Psych Consultant',
      value: 'no-psych-consultant'
    }
  ]
)

export const cocmDischargeReason$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.cocmDischargeReason
)

export const accountManagers$ = createSelector(
  companyReferenceTableSelector,
  (refs) => refs.accountManager
)

export const companyAccountManagersSelector$ = createSelector(
  companyReferenceTableSelector,
  ({ accountManager = [] }) =>
    accountManager.map((item) => ({
      ...item,
      label: item.refValue,
      title: item.refValue,
      value: item.uuid,
      key: item.uuid
    }))
)

export const companyAccountManagersSelectorWithNo$ = createSelector(
  companyAccountManagersSelector$,
  (accountManager) => [
    ...accountManager,
    {
      label: 'No Account Manager',
      value: 'no-account-manager'
    }
  ]
)

export const activityDashboardNoteStatuses$ = createSelector(
  companyReferenceTableSelector,
  ({ activityDashboardNoteStatuses = [] }) => activityDashboardNoteStatuses
)

export const advanceDirectiveConsentType$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.advanceDirectiveConsentType)
)

export const advanceDirectiveConsentCommunicationMethod$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.advanceDirectiveConsentCommunicationMethod)
)

export const advanceDirectiveConsentStatus$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.advanceDirectiveConsentStatus)
)

export const advanceDirectiveConsentDirectives$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.advanceDirectiveConsentDirectives)
)

export const careGiverCaseManager$ = createSelector(
  companyReferenceTableSelector,
  (refs) => convertDataFields(refs.careGiverCaseManager)
)

export const careGiverCaseManagerWithAll$ = createSelector(
  careGiverCaseManager$,
  (items = []) =>
    addAllValueV5({
      list: [
        ...items,
        { value: 'no-case-manager', title: 'No Caregiver Case Manager' }
      ]
    })
)

export const careGiverCaseManagerDropdown$ = createSelector(
  companyReferenceTableSelector,
  ({ careGiverCaseManager = [] }) =>
    careGiverCaseManager.map((item) => ({
      ...item,
      label: item.refValue,
      title: item.refValue,
      value: item.uuid,
      key: item.uuid
    }))
)
